import React, { Component } from "react";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import Actions from "../redux/actions/index";
import AppLoader from "../utils/AppLoader";
import stringTamil from "../utils/stringTamil";
import stringEnglish from "../utils/stringEnglish";
import SRM_GLOBAL from "../assets/img/srm-global.jpeg";
import { toast } from "react-toastify";
import {
  ensurePatientMediaPermission,
  patientMediaPermissionMessage,
  patientMediaPermissionTitle,
  isPatientMediaAlreadyGranted,
  patientPermissionGuideSteps,
  detectPatientBrowser,
} from "../utils/patientMediaPermission";

class SRM_IP_Customer extends Component {
  constructor(props) {
    super(props);
    this.state = {
      error: {},
      message: {},
      vendorLogo: null,
      language: parseInt(localStorage.getItem("language")) || 1,
      category: "SRM_HOSPITAL",
      roomId: '',
      RoomServiceList: [],
      encryptRoomId: "",
      skipServicePage: false,
      isImmediateConnect: false,
      loadError: false,
      mediaPermissionBlocked: false,
      mediaPermissionReason: '',
      pendingJoinItem: null,
      /** Waiting on browser Allow dialog — do NOT cover with full-page loader */
      isRequestingMedia: false,
      permGuidePulse: false,
    };
    this._autoJoinStarted = false;
    this._joinInFlight = false;
    this._onWindowFocus = this._onWindowFocus.bind(this);
  }

  async componentDidMount() {
    localStorage?.clear();
    const roomId = this.props.match.params.roomId;
    const vendorId = this.props.match.params.vendor_id;
    const isOldFlow = !vendorId;

    if (!roomId || roomId.trim() === '') {
      if (vendorId) {
        this.props.history.push(`/SRM_IP/patient/${vendorId}`);
      } else {
        this.props.history.push('/pageNotFound');
      }
      return;
    }

    this.setState({ roomId: roomId, vendorId: vendorId, isOldFlow: isOldFlow });
    localStorage.setItem("srm_ip_room", roomId);
    if (vendorId) {
      localStorage.setItem("srm_ip_vendor_id", vendorId);
    }
    this.getRoomService(roomId);
    this.setState({ encryptRoomId: roomId });

    window.addEventListener('focus', this._onWindowFocus);
    document.addEventListener('visibilitychange', this._onWindowFocus);
  }

  componentWillUnmount() {
    window.removeEventListener('focus', this._onWindowFocus);
    document.removeEventListener('visibilitychange', this._onWindowFocus);
  }

  /** After patient unlocks Camera in browser settings and returns — auto continue. */
  _onWindowFocus = async () => {
    if (document.visibilityState && document.visibilityState !== 'visible') {
      return;
    }
    if (!this.state.mediaPermissionBlocked || this._joinInFlight || this.state.isRequestingMedia) {
      return;
    }
    const granted = await isPatientMediaAlreadyGranted();
    if (!granted) {
      return;
    }
    this.retryMediaPermissionAndJoin();
  };

  getRoomService = (encryptRoomId) => {
    const params = { encryptRoomId };
    const callback = (response) => {
      this.setState({ isLoading: false });
      if (!response || typeof response?.response_code !== 'number') {
        this.setState({ loadError: true, skipServicePage: false });
        toast.error('Unable to load. Please try again.');
        return;
      }
      if (response?.response_code === 0) {
        const roomData = response?.response || {};
        const mappings = (roomData?.SrmIpRoomServiceMappings || []).filter(
          (item) =>
            Number(item?.isActive) === 1 &&
            Number(item?.SrmIpServiceMaster?.isActive) === 1
        );
        const immediate = Number(roomData?.Vendor?.isSrmIpImmediateConnect) === 1;
        const fromFeedback = new URLSearchParams(this.props.location?.search || '').get('fromFeedback') === '1';
        const wardUserId = roomData?.SrmIpWard?.headUserId;
        const firstReadyService = mappings.find((item) => {
          if (!item?.SrmIpServiceMaster || Number(item?.isActive) !== 1 || Number(item?.SrmIpServiceMaster?.isActive) !== 1) {
            return false;
          }
          const roomNurseId = item?.userId;
          const headId = item?.SrmIpServiceMaster?.userId;
          const wardId = item?.SrmIpRoom?.SrmIpWard?.headUserId || wardUserId;
          return !!(roomNurseId || wardId || headId);
        });

        this.setState({
          RoomServiceList: mappings,
          isImmediateConnect: immediate,
        });
        if (wardUserId) {
          localStorage.setItem('srm_ip_ward_user_id', String(wardUserId));
        }
        localStorage.setItem(
          'srm_ip_auto_redirect_to_head',
          Number(roomData?.Vendor?.isSrmIpAutoRedirectToHead) === 1 ? '1' : '0'
        );
        const floorToWardSec = parseInt(roomData?.Vendor?.srmIpFloorToWardSec, 10);
        const wardToHeadSec = parseInt(roomData?.Vendor?.srmIpWardToHeadSec, 10);
        const headRingSec = parseInt(roomData?.Vendor?.srmIpHeadRingTimeoutSec, 10);
        localStorage.setItem(
          'srm_ip_floor_to_ward_sec',
          String(Number.isFinite(floorToWardSec) && floorToWardSec >= 5 ? floorToWardSec : 15)
        );
        localStorage.setItem(
          'srm_ip_ward_to_head_sec',
          String(Number.isFinite(wardToHeadSec) && wardToHeadSec >= 5 ? wardToHeadSec : 25)
        );
        localStorage.setItem(
          'srm_ip_head_ring_timeout_sec',
          String(Number.isFinite(headRingSec) && headRingSec >= 5 ? headRingSec : 20)
        );

        if (immediate && firstReadyService && !fromFeedback && !this._autoJoinStarted) {
          this._autoJoinStarted = true;
          this.setState({ skipServicePage: true });
          this.join(firstReadyService, { fromAutoConnect: true });
          return;
        }

        this.setState({ skipServicePage: false, loadError: false });
      } else {
        this.setState({ loadError: true, skipServicePage: false });
        toast.error(typeof response?.response === 'string' ? response.response : 'Unable to load room services.');
      }
    };
    this.setState({ isLoading: true });
    this.props.getSrmIpRoomService(params, callback, true);
  };

  join = async (item, options = {}) => {
    if (!item) {
      toast.error('Please tap a service to start the call.');
      return;
    }
    if (this._joinInFlight) {
      return;
    }
    this._joinInFlight = true;

    // Never cover the browser Allow popup with full-page loader
    this.setState({
      isRequestingMedia: true,
      pendingJoinItem: item,
      permGuidePulse: false,
    });

    let perm;
    try {
      // This is what opens the browser "Allow" box (when the browser still allows it)
      perm = await ensurePatientMediaPermission();
    } catch (e) {
      perm = { ok: false, reason: 'error' };
    }

    if (!perm.ok) {
      this._joinInFlight = false;
      this._autoJoinStarted = false;
      const reason = perm.reason || 'denied';
      this.setState({
        isLoading: false,
        isRequestingMedia: false,
        skipServicePage: false,
        mediaPermissionBlocked: true,
        mediaPermissionReason: reason,
        pendingJoinItem: item,
        permGuidePulse: reason === 'blocked',
      });
      // User expects Allow popup — explain when browser refuses to show it
      if (reason === 'blocked') {
        toast.info('Allow popup is blocked by the browser. Turn Camera ON using the steps above, then tap Allow & Call.');
      } else if (reason === 'inapp') {
        toast.info('Open this link in Chrome or Safari to start the video call.');
      } else if (reason === 'denied') {
        toast.info('Please tap Allow in the browser popup.');
      } else {
        toast.error(patientMediaPermissionMessage(reason));
      }
      return;
    }

    this.setState({
      isRequestingMedia: false,
      mediaPermissionBlocked: false,
      mediaPermissionReason: '',
      isLoading: true,
      permGuidePulse: false,
    });

    const headUserId = item?.SrmIpServiceMaster?.userId;
    const wardUserId = item?.SrmIpRoom?.SrmIpWard?.headUserId
      || localStorage.getItem('srm_ip_ward_user_id');
    const roomNurseUserId = item?.userId;
    let ringUserId = roomNurseUserId;
    let isWardEscalation = 0;
    let isHeadEscalation = 0;
    if (!roomNurseUserId) {
      if (wardUserId) {
        ringUserId = wardUserId;
        isWardEscalation = 1;
      } else if (headUserId) {
        ringUserId = headUserId;
        isHeadEscalation = 1;
      }
    }
    const params = {
      encryptRoomId: this.state.encryptRoomId,
      srmIpServiceId: item?.srmIpServiceId,
      srmIpRoomId: item?.srmIpRoomId,
      userId: ringUserId,
      ...(isWardEscalation ? { isWardEscalation: 1 } : {}),
      ...(isHeadEscalation ? { isHeadEscalation: 1 } : {}),
    };
    localStorage.setItem("srmIpServiceId", item?.srmIpServiceId);
    localStorage.setItem("srm_ip_mapping_room_id", item?.srmIpRoomId);
    localStorage.setItem("srm_ip_encrypt_room_id", this.state.encryptRoomId);
    if (headUserId) {
      localStorage.setItem("srm_ip_head_user_id", String(headUserId));
    }
    if (wardUserId) {
      localStorage.setItem('srm_ip_ward_user_id', String(wardUserId));
    }
    localStorage.setItem("srm_patient_call_started_at", String(Date.now()));
    let srmCallSessionId = localStorage.getItem('srm_call_session_id');
    if (!srmCallSessionId) {
      srmCallSessionId = (typeof crypto !== 'undefined' && crypto.randomUUID)
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      localStorage.setItem('srm_call_session_id', srmCallSessionId);
    }
    params.srmCallSessionId = srmCallSessionId;
    this.props.customerListAction(params, (res) => {
      this._joinInFlight = false;
      this.setState({ isLoading: false });
      if (!res || typeof res?.response_code !== 'number') {
        toast.error('Connection failed. Please try again.');
        if (options.fromAutoConnect) {
          this._autoJoinStarted = false;
          this.setState({ skipServicePage: false });
        }
        return;
      }
      if (res?.response_code === 0) {
        const roomId = res?.response?.roomId;
        if (res?.response?.srmCallSessionId) {
          localStorage.setItem('srm_call_session_id', String(res.response.srmCallSessionId));
        }
        const tier = String(res?.response?.srmEscalationTier || '').toLowerCase();
        if (tier === 'head') {
          this.props.history.push('/srm_head/' + roomId);
        } else if (tier === 'ward') {
          this.props.history.push('/srm_ward/' + roomId);
        } else {
          this.props.history.push('/customer/' + roomId);
        }
      } else if (res?.response_code === 5) {
        toast.error(typeof res?.response === 'string' ? res.response : 'Unable to connect.');
        if (options.fromAutoConnect) {
          this._autoJoinStarted = false;
          this.setState({ skipServicePage: false });
        }
      } else if (options.fromAutoConnect) {
        this._autoJoinStarted = false;
        this.setState({ skipServicePage: false });
        toast.error(typeof res?.response === 'string' ? res.response : 'Unable to connect. Please select a service.');
      } else {
        this.props.history.push('/pageNotFound');
      }
    });
  };

  /** Opens browser camera/mic Allow popup again (when browser allows it). */
  retryMediaPermissionAndJoin = () => {
    const item = this.state.pendingJoinItem
      || (this.state.RoomServiceList || []).find((row) => row?.SrmIpServiceMaster)
      || (this.state.RoomServiceList || [])[0];
    if (!item) {
      toast.error('Please tap a service below.');
      this.setState({ mediaPermissionBlocked: false, skipServicePage: false });
      return;
    }
    // Reset so a stuck flag never blocks the Allow popup attempt
    this._joinInFlight = false;
    this.join(item, { fromAutoConnect: !!this.state.isImmediateConnect });
  };

  renderPermissionGuide = (isTamil) => {
    const steps = patientPermissionGuideSteps(isTamil);
    return (
      <div className={`srm-perm-guide text-start mt-2${this.state.permGuidePulse ? ' srm-perm-guide-pulse' : ''}`}>
        <div className="srm-perm-browser-bar" aria-hidden="true">
          <span className="srm-perm-lock">
            <i className="fa-solid fa-lock" />
          </span>
          <span className="srm-perm-url">hospital…</span>
        </div>
        <ol className="srm-perm-steps mb-0">
          {steps.map((text, idx) => (
            <li key={idx}>{text}</li>
          ))}
        </ol>
      </div>
    );
  };

  copyPageLink = async () => {
    const url = window.location.href;
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        const input = document.createElement('input');
        input.value = url;
        document.body.appendChild(input);
        input.select();
        document.execCommand('copy');
        document.body.removeChild(input);
      }
      toast.success('Link copied. Paste it in Chrome or Safari.');
    } catch (_e) {
      toast.info(url);
    }
  };

  render() {
    const {
      vendorLogo,
      language,
      isLoading,
      RoomServiceList,
      skipServicePage,
      loadError,
      mediaPermissionBlocked,
      mediaPermissionReason,
      isRequestingMedia,
    } = this.state;
    const MESSAGE = language === 2 ? stringTamil?.MESSAGE : stringEnglish?.MESSAGE;
    const isTamil = language === 2;
    const showGuide = mediaPermissionReason === 'blocked';
    const showInAppHelp = mediaPermissionReason === 'inapp' || mediaPermissionReason === 'unsupported';
    const allowCallLabel = isRequestingMedia
      ? (isTamil ? 'காத்திருக்கவும்…' : 'Opening Allow…')
      : 'Allow & Call';
    const browser = detectPatientBrowser();

    return (
      <div
        className="customer-care overflow-auto"
        style={{ backgroundImage: `url(${SRM_GLOBAL})`, backgroundSize: "auto 100%", backgroundPosition: "center" }}
      >
        {isLoading && !isRequestingMedia && <AppLoader />}
        <div className="row call-row mx-0">
          <div className="col-md-12 d-flex flex-column justify-content-center align-items-center">
            <div className="text-center">
              <img
                className="logo"
                src={vendorLogo ? vendorLogo : require("../assets/img/srm-logo.png")}
                alt="logo"
              />
            </div>

            {mediaPermissionBlocked && (
              <div
                className="d-flex flex-column gap-2 width_value p-sm-5 p-3 rounded-4 mb-3"
                style={{ backgroundColor: "rgb(255 255 255 / 80%)", maxWidth: 480 }}
              >
                <h5 className="mb-0 text-center" style={{ color: '#6B4A98', fontWeight: 700 }}>
                  {patientMediaPermissionTitle(mediaPermissionReason)}
                </h5>
                <p className="mb-0 text-center" style={{ color: '#334155', lineHeight: 1.4 }}>
                  {patientMediaPermissionMessage(mediaPermissionReason)}
                </p>
                {showGuide && this.renderPermissionGuide(isTamil)}
                {showInAppHelp && (
                  <div className="srm-perm-guide text-start mt-2">
                    <ol className="srm-perm-steps mb-0">
                      <li>{isTamil ? 'கீழே Copy Link என்பதைத் தட்டவும்' : 'Tap Copy Link below'}</li>
                      <li>
                        {browser.isIOS
                          ? (isTamil ? 'Safari-ல் திறந்து link-ஐ paste செய்யவும்' : 'Open Safari and paste the link')
                          : (isTamil ? 'Chrome-ல் திறந்து link-ஐ paste செய்யவும்' : 'Open Chrome and paste the link')}
                      </li>
                      <li>{isTamil ? 'பின் சேவையைத் தேர்ந்தெடுத்து அழைக்கவும்' : 'Then choose a service and call'}</li>
                    </ol>
                  </div>
                )}
                {showInAppHelp ? (
                  <button
                    type="button"
                    className="btn btn-gradient mt-2"
                    style={{ fontWeight: 700, minHeight: 48 }}
                    onClick={this.copyPageLink}
                  >
                    <i className="fa-solid fa-link me-2" aria-hidden="true" />
                    Copy Link
                  </button>
                ) : (
                  <button
                    type="button"
                    className="btn btn-gradient mt-2"
                    style={{ fontWeight: 700, minHeight: 48 }}
                    disabled={isRequestingMedia}
                    onClick={this.retryMediaPermissionAndJoin}
                  >
                    <i className="fa-solid fa-video me-2" aria-hidden="true" />
                    {allowCallLabel}
                  </button>
                )}
              </div>
            )}

            {!mediaPermissionBlocked && !skipServicePage && (
              <div className="d-flex flex-column gap-3 width_value pb-sm-0 pb-5">
                <div
                  className="d-flex flex-column gap-3 p-sm-5 p-3 rounded-4"
                  style={{ backgroundColor: "rgb(255 255 255 / 80%)" }}
                >
                  <div>
                    <h1 className="text-center">{MESSAGE?.HOW_CAN_I_HELP}</h1>
                    <p className="text-center mb-0" style={{ color: '#64748b', fontSize: 15 }}>
                      {MESSAGE?.VIDEO_CALL_HELP_SUB}
                    </p>
                  </div>
                  <div className="d-flex flex-column">
                    <label className="form-label form-text">
                      {MESSAGE?.SERVICES}
                    </label>
                    <div className="row g-2">
                      {RoomServiceList?.length > 0 ? RoomServiceList.map((item) => (
                        <div className="col-12 col-sm-6" key={item?.id}>
                          <button
                            type="button"
                            className="cat-btn btn-gradient srm-service-call-btn"
                            disabled={isRequestingMedia || isLoading}
                            onClick={() => this.join(item)}
                          >
                            <span className="srm-service-call-name">
                              {item?.SrmIpServiceMaster?.serviceName}
                            </span>
                            <span className="srm-service-call-icon" title="Video call" aria-hidden="true">
                              <i className="fa-solid fa-video" />
                            </span>
                          </button>
                        </div>
                      )) : loadError ? (
                        <div className="text-center mb-0">
                          <p className="mb-2">Could not load services. Please try again.</p>
                          <button
                            type="button"
                            className="cat-btn btn-gradient"
                            onClick={() => this.getRoomService(this.state.encryptRoomId || this.state.roomId)}
                          >
                            Retry
                          </button>
                        </div>
                      ) : (
                        <p className="text-center mb-0">No service available for this room. Please contact staff.</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {!mediaPermissionBlocked && skipServicePage && !isLoading && (
              <div
                className="p-4 rounded-4 text-center"
                style={{ backgroundColor: "rgb(255 255 255 / 80%)", maxWidth: 420 }}
              >
                <h2 style={{ fontSize: '1.35rem', color: '#6B4A98' }}>
                  {isRequestingMedia
                    ? (isTamil ? 'கேமராவை அனுமதிக்கவும்…' : 'Allow camera when asked…')
                    : (isTamil ? 'இணைக்கிறோம்…' : 'Connecting…')}
                </h2>
                <p className="mb-0 text-muted">
                  {isRequestingMedia
                    ? (isTamil ? 'Browser-ல் Allow என்பதைத் தட்டவும்' : 'Tap Allow in the browser popup')
                    : (isTamil ? 'சற்று காத்திருக்கவும்' : 'Please wait')}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
}

export const mapStateToProps = (state) => {
  return {
    customerListLoader: state?.CUSTOMER_LIST,
  };
};

export default withRouter(connect(mapStateToProps, {
  customerListAction: Actions.cutomerListAction,
  getSrmIpRoomService: Actions.srmIpRoomServiceListAction,
})(SRM_IP_Customer));
